import { ICCAsset } from 'src/interfaces/Assets';
import { IGcfComponentConfig } from 'src/interfaces/Component';
import { IThemeRes } from 'src/interfaces/GCFThemes';
import {
  IGcfProductListProduct,
  IGcfProductListProductImage,
  IGcfProductListProductSize,
  IGcfProductListProductSizeVariation,
  IProductVariation,
} from 'src/interfaces/Product';
import { lang } from 'src/utils';
import {
  DEFAULT_SORT_ORDER,
  isProductImageVisible,
  updateFlavorImagesVisibility,
} from 'src/utils/product-attributes';

import { IProductVariationsAssets } from '../../MenuBoardModificationPage.interface';

export { DEFAULT_SORT_ORDER };

export const NONE_LABEL = lang.none;

export interface IAttributeFlavor {
  key: string;
  name: string;
  productCode: string;
  checked: boolean;
  order: number;
  images?: IGcfProductListProductImage[];
}

export interface IAttributeSize {
  key: string;
  name: string;
  checked: boolean;
  order: number;
  flavors: IAttributeFlavor[];
}

export const getPreviewAssetNames = (
  localAttributes: IAttributeSize[],
  productVariationsAssets: IProductVariationsAssets,
) =>
  Array.from(
    new Set(
      [
        ...localAttributes.flatMap((size) =>
          size.flavors.flatMap((flavor) =>
            (flavor.images ?? []).map((image) => image.assetName),
          ),
        ),
        ...Object.values(productVariationsAssets).flatMap((assets) =>
          assets.map((asset) => asset.name),
        ),
      ].filter((assetName): assetName is string => Boolean(assetName)),
    ),
  );

export const getDefaultFlavorForSize = (
  attributes: IAttributeSize[],
  defaultFlavor?: string,
  sizeName?: string,
) => {
  const targetSize = attributes.find((size) => size.name === sizeName);

  return targetSize?.flavors.find(
    (flavor) => flavor.productCode === defaultFlavor,
  )?.productCode;
};

export const getNewlyCheckedFlavor = (
  currentAttributes: IAttributeSize[],
  updatedAttributes: IAttributeSize[],
) => {
  for (const updatedSize of updatedAttributes) {
    const currentSize = currentAttributes.find(
      (size) => size.name === updatedSize.name,
    );

    const newlyCheckedFlavor = updatedSize.flavors.find((updatedFlavor) => {
      const currentFlavor = currentSize?.flavors.find(
        (flavor) => flavor.productCode === updatedFlavor.productCode,
      );

      return updatedFlavor.checked && !currentFlavor?.checked;
    });

    if (newlyCheckedFlavor) {
      return {
        sizeName: updatedSize.name,
        flavorProductCode: newlyCheckedFlavor.productCode,
      };
    }
  }

  return undefined;
};

const resolveVariationImages = (
  variation: IProductVariation,
  configVariationEntry?: IGcfProductListProductSizeVariation,
  themeName?: string,
  themes: IThemeRes[] = [],
): IGcfProductListProductImage[] => {
  // Tier 1: Config-level images (filter out style-only images without assetName)
  const configImages = configVariationEntry?.config?.images?.filter(
    (img) => img.assetName,
  );
  if (configImages?.length) {
    return configImages;
  }

  // Tier 2: Themed assets from CC-BE data — match by theme_id
  const themeId = themes.find((t) => t.name === themeName)?.id;
  const themedAssets = variation.assets?.filter((a: ICCAsset) => {
    if (!themeId) return false;

    return a.theme_id?.includes(themeId);
  });

  if (themedAssets?.length) {
    return themedAssets.map((a) => ({ assetName: a.name, visible: false }));
  }

  if (themeName) {
    return [];
  }

  if (variation.assets?.length) {
    return variation.assets.map((a) => ({ assetName: a.name, visible: false }));
  }

  return [];
};

export const mapVariationsToAttributes = (
  variations: IProductVariation[],
  productConfig?: IGcfProductListProduct,
  themeName?: string,
  themes: IThemeRes[] = [],
): IAttributeSize[] => {
  // 1. Build config lookup maps
  const configSizes = productConfig?.sizes ?? {};
  const configSizeMap = new Map<string, IGcfProductListProductSize>();
  for (const s of Object.values(configSizes) as IGcfProductListProductSize[]) {
    const nameKey = s.name ?? '';
    configSizeMap.set(nameKey, s);
  }

  // 2. Group variations by size name. Only active variations are selectable
  // in the override sidebar — inactive catalog variations must not surface here.
  const sizeMap = new Map<string, IProductVariation[]>();
  for (const v of variations) {
    if (!v.active) continue;
    const sizeName = v.productVariationSize?.name ?? '';
    if (!sizeMap.has(sizeName)) sizeMap.set(sizeName, []);
    sizeMap.get(sizeName)!.push(v);
  }

  // 4. Build IAttributeSize[] with checked state, images, sort order
  const attributeSizes: IAttributeSize[] = Array.from(sizeMap.entries()).map(
    ([sizeName, sizeVariations], sizeIndex) => {
      const matchingConfigSize = configSizeMap.get(sizeName);
      const sizeChecked = !!matchingConfigSize;

      const configVariationKeys = matchingConfigSize?.variations
        ? new Set(
            Object.values(matchingConfigSize.variations).map(
              (v) => (v as IGcfProductListProductSizeVariation).productCode,
            ),
          )
        : null;

      const orderMap = new Map<string, number>();
      if (matchingConfigSize?.variations) {
        for (const v of Object.values(
          matchingConfigSize.variations,
        ) as IGcfProductListProductSizeVariation[]) {
          orderMap.set(v.productCode, v.order ?? DEFAULT_SORT_ORDER);
        }
      }

      const flavors: IAttributeFlavor[] = sizeVariations.map((v, i) => {
        const flavorName = v.productVariationFlavor?.name ?? '';
        const variationProductCode = v.product_code;

        // Find config entry for this variation
        const configVariationEntry = matchingConfigSize?.variations
          ? (
              Object.values(
                matchingConfigSize.variations,
              ) as IGcfProductListProductSizeVariation[]
            ).find((cfg) => cfg.productCode === variationProductCode)
          : undefined;

        // Use config order for checked flavors; catalog index for unchecked
        const configOrder = orderMap.get(variationProductCode);
        const flavorOrder = configOrder !== undefined ? configOrder : i;

        return {
          key: flavorName || NONE_LABEL,
          name: flavorName,
          productCode: variationProductCode,
          checked:
            sizeChecked &&
            Boolean(configVariationKeys?.has(variationProductCode)),
          order: flavorOrder,
          images: resolveVariationImages(
            v,
            configVariationEntry,
            themeName,
            themes,
          ),
        };
      });

      // Sort flavors by order — preserves relative position of checked and
      // unchecked flavors. Unchecked flavors get catalog-index order so they
      // interleave correctly with checked flavors that have config order.
      flavors.sort((a, b) => a.order - b.order);

      return {
        key: sizeName || NONE_LABEL,
        name: sizeName,
        checked: sizeChecked,
        order: matchingConfigSize?.order ?? sizeIndex,
        flavors,
      };
    },
  );

  // 5. Sort sizes by order — preserves relative position of checked and
  // unchecked sizes. Checked sizes get config order; unchecked sizes get
  // their catalog-index position so they interleave correctly.
  attributeSizes.sort((a, b) => a.order - b.order);

  return attributeSizes;
};

export const getProductImageFromConfig = (
  productConfig?: IGcfProductListProduct,
  mainProductAssetName?: string,
): { visible: boolean; assetName?: string } => {
  const visible = isProductImageVisible(productConfig);

  return {
    visible,
    ...(mainProductAssetName !== undefined && {
      assetName: mainProductAssetName,
    }),
  };
};

export const getDefaultImageFromAttributes = (
  attributeSizes: IAttributeSize[],
  defaultSize?: string,
  fallbackAssetName?: string,
  defaultFlavor?: string,
): IGcfProductListProductImage | undefined => {
  if (attributeSizes.length === 0) {
    return fallbackAssetName
      ? { assetName: fallbackAssetName, visible: true }
      : undefined;
  }

  const resolveFlavorImage = (
    size: IAttributeSize,
    targetFlavorProductCode?: string,
  ) => {
    // Prefer the explicitly selected default flavor, then first checked, then first
    const targetFlavor = targetFlavorProductCode
      ? size.flavors.find(
          (f) => f.checked && f.productCode === targetFlavorProductCode,
        )
      : undefined;
    const firstChecked = size.flavors.find((f) => f.checked);
    const flavor = targetFlavor ?? firstChecked ?? size.flavors[0];
    if (!flavor) return undefined;

    return (
      flavor.images?.find((img) => img.visible && img.assetName) ??
      flavor.images?.find((img) => img.assetName)
    );
  };

  if (defaultSize !== undefined) {
    const normalizedSize = defaultSize === NONE_LABEL ? '' : defaultSize;
    const targetSize = attributeSizes.find((s) => s.name === normalizedSize);
    if (targetSize) {
      const normalizedFlavor =
        defaultFlavor === NONE_LABEL ? '' : defaultFlavor;
      const image = resolveFlavorImage(targetSize, normalizedFlavor);
      if (image) return image;

      // defaultSize is explicit — fall back to the product-level asset, never
      // another size's image (would otherwise show the wrong size's thumbnail).
      return fallbackAssetName
        ? { assetName: fallbackAssetName, visible: true }
        : undefined;
    }
  }

  const firstSize = attributeSizes[0];
  if (firstSize) {
    const image = resolveFlavorImage(firstSize);
    if (image) return image;
  }

  return fallbackAssetName
    ? { assetName: fallbackAssetName, visible: true }
    : undefined;
};

export const updateFlavorImages = (
  flavors: IAttributeFlavor[],
): IAttributeFlavor[] => {
  // Only `checked` flavors (those present in component_config) are eligible
  // to be the default visible flavor. Catalog-only (unchecked) entries carry
  // a loop-index fallback `order` that is NOT a real component_config order
  // and would otherwise hijack visibility from the legitimate first-checked.
  const firstCheckedByOrder = [...flavors]
    .filter((f) => f.checked)
    .sort((a, b) => a.order - b.order)[0];
  const firstProductCode = firstCheckedByOrder?.productCode;

  return updateFlavorImagesVisibility(
    flavors,
    (f) => f.productCode === firstProductCode,
  );
};

export const applyImageVisibility = (
  sizes: IAttributeSize[],
  targetDefaultSize?: string,
  defaultFlavor?: string,
): IAttributeSize[] =>
  sizes.map((s) => ({
    ...s,
    flavors:
      targetDefaultSize !== undefined && s.name === targetDefaultSize
        ? defaultFlavor !== undefined
          ? updateFlavorImagesVisibility(
              s.flavors,
              (f) => f.checked && f.productCode === defaultFlavor,
            )
          : updateFlavorImages(s.flavors)
        : s.flavors.map((f) => ({
            ...f,
            images: (f.images ?? []).map((img) => ({
              ...img,
              visible: false,
            })),
          })),
  }));

export const applyVisibilityToFlavor = (
  sizes: IAttributeSize[],
  targetSizeName: string,
  targetFlavorProductCode: string,
): IAttributeSize[] => {
  return sizes.map((s) => ({
    ...s,
    flavors:
      s.name === targetSizeName
        ? updateFlavorImagesVisibility(
            s.flavors,
            (f) => f.productCode === targetFlavorProductCode,
          )
        : s.flavors.map((f) => ({
            ...f,
            images: (f.images ?? []).map((img) => ({
              ...img,
              visible: false,
            })),
          })),
  }));
};

export const hasProductAttributeValidationError = (
  componentConfig: IGcfComponentConfig | undefined,
): boolean => {
  const groups = componentConfig?.productList?.groups ?? {};
  const products = Object.values(groups).flatMap((group) =>
    Object.values(group.products ?? {}),
  );

  // No products at all
  if (products.length === 0) return true;

  for (const product of products) {
    const sizes = product.sizes ? Object.values(product.sizes) : [];

    // No sizes
    if (sizes.length === 0) return true;

    // No size with any variations
    const hasAnyVariations = sizes.some(
      (size) => Object.keys(size.variations ?? {}).length > 0,
    );
    if (!hasAnyVariations) return true;

    // defaultSize references a size with zero variations
    const defaultSize = product.config?.defaultSize;
    if (defaultSize !== undefined) {
      const matchedSize = sizes.find((s) => (s.name ?? '') === defaultSize);
      if (
        matchedSize &&
        Object.keys(matchedSize.variations ?? {}).length === 0
      ) {
        return true;
      }
    }
  }

  return false;
};
